import React, { useState } from 'react'
import { Card, CardBody, CardFooter, Col, Label, Modal, ModalBody, ModalHeader, Row } from 'reactstrap';
import CustomToast from '../../../utils/CustomToast';
import DownloadIcon from 'assets/img/bis/download-icon.png';
import axios from 'axios';
import useSiteCookies from '../useSiteCookies';

const FileUploaderModal = ({ show, onHide, instance, id, onData }) => {
    const instanceType = instance.instance;
    const instanceId = instance.id;
    const instancePDFType = instance.type || '';

    const { currentUser, token } = useSiteCookies();

    const [formStatus, setFormStatus] = useState(false);
    const [uploadedFileError, setUploadedFileError] = useState(false);
    const [uploadedFile, setUploadedFile] = useState('Upload file here');

    const [documentFormDataId, setDocumentFormDataId] = useState(0);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [item, setItem] = useState({ name: '', value: '' });

    const allowedFileTypes = {
        image: ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'],
        video: ['video/mp4', 'video/quicktime', 'video/x-msvideo'], // quicktime = mov, x-msvideo = avi
        audio: ['audio/mpeg', 'audio/wav'], // mpeg = mp3
        document: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv'],
        pdf: ['application/pdf']
    };

    const uploadInstanceLabel = {
        document: 'DOCUMENT',
        pdf: 'DOCUMENT',
        photo_1: 'PHOTO 1',
        photo_2: 'PHOTO 2',
        image: 'IMAGE',
        video: 'VIDEO',
        audio: 'AUDIO',
    };

    const hiddenFileInputPdf = React.useRef(null);

    const postFile = async (data) => {
        return await axios.post(
            process.env.REACT_APP_API_ENDPOINT + `category/question/upload`, data, {
            params: {
                user_id: currentUser,
                token: token
            }
        });
    }

    const postChunk = async (data, onUploadProgress) => {
        return await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'store/entry/file/chunk',
            data,
            {
            params: { user_id: currentUser, token: token },
            timeout: 20000, // ⏱ 10s timeout
            onUploadProgress,
            }
        );
    };

    const postCombineChunk = async (data) => {
        return await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'combine/entry/file/chunk',
            data,
            {
            params: { user_id: currentUser, token: token },
            timeout: 20000,
            }
        );
    };


    const submitDocument = async (file, filename, count, onUploadProgress) => {
        const dataArray = new FormData();
        dataArray.append("file", file);
        dataArray.append("filename", filename);
        dataArray.append("index", count);

        try {
            return await postChunk(dataArray, onUploadProgress);
        } catch (error) {
            throw error; // fail fast, will be caught in validateFile
        }
    };


    const combineDocument = async (filename, extension, totalChunks) => {
        try {
            const data = { filename, extension, total_chunks: totalChunks };
            const response = await postCombineChunk(data);

            if (response.data.status === "Success") {
                setFormStatus(false);
                setUploadedFile(filename);

                const fileUrl = response.data.data.url;
                if (fileUrl) {
                    setItem({ name: "File", value: fileUrl });
                }
            } else {
                setUploadedFile("Upload file here");
                CustomToast.warning("Upload failed. Please check your internet connection and try again.");
                setFormStatus(false);
                CustomToast.defaultError();
                // throw new Error("Combine failed");
            }
        } catch (error) {
            // setUploadedFileError(true);
            setUploadedFile("Upload file here");
            CustomToast.warning("Upload failed. Please check your internet connection and try again.");
            setFormStatus(false);
            CustomToast.defaultError();
        }
    };


    const handleClickFile = () => {
        hiddenFileInputPdf.current.click();
    };

    const handleDragEnter = e => {
        e.preventDefault();
    };

    const handleDragOver = e => {
        e.preventDefault();
    };

    const handleDragLeave = e => {
        e.preventDefault();
    };

    const handleDrop = e => {
        e.preventDefault();
    
        const droppedFiles = e.dataTransfer.files;
        const totalFilesCount = droppedFiles.length;
    
        if (totalFilesCount > 1) {
            const message = "This field accepts only 1!";
            CustomToast.warning(message);
            return;
        }
    
        if (formStatus)
            return;
    
        setFormStatus(true);
    
        if (droppedFiles[0]) {
            const file = droppedFiles[0];
            const size = file.size;
        
            if (instanceType === 'image') {
                imageValidations(file);
            } else if (instanceType === 'video') {
                videoValidations(file);
            } else if (instanceType === 'audio') {
                audioValidations(file);
            } else if (instancePDFType === 'pdf') {
                documentPDFValidations(file);
            } else if (instanceType === 'document') {
                documentValidations(file);
            }
        }
    };

    const handleChangeFiles = ({ target }) => {
        if (target.files.length < 1 || !target.validity.valid) {
            return;
        }

        setFormStatus(true);

        const file = target.files[0];

        if (instanceType === "image") {
            validateFile(file, "image");
        } else if (instanceType === "video") {
            validateFile(file, "video");
        } else if (instanceType === "audio") {
            validateFile(file, "audio");
        } else if (instancePDFType === "pdf") {
            validateFile(file, "pdf");
        } else if (instanceType === "document") {
            validateFile(file, "document");
        }

        target.value = "";
    };


    const validateFile = async (file, type) => {
        if (!allowedFileTypes[type].includes(file.type)) {
            setFormStatus(false);
            CustomToast.warning(`Please upload a valid ${type} file!`);
            return;
        }

        const size = file.size;
        if (instancePDFType === 'pdf') {
            const max_size = 51 * 1024 * 1024;
            if (size > max_size) {
                CustomToast.warning(`Please upload a PDF smaller than 50 MB!`);
                setFormStatus(false);
                return;
            }
        } else {
            const max_size = 151 * 1024 * 1024;
            if (size > max_size) {
                CustomToast.warning(`Please upload a file smaller than 150 MB!`);
                setFormStatus(false);
                return;
            }
        }

        setUploadedFile("Uploading file...");
        setUploadedFileError(false);

        const fileName = new Date().getTime().toString() + currentUser.toString();
        const extension = file.name.split(".").pop();
        const chunkSize = 1024 * 1024; // 1MB
        const totalChunks = Math.ceil(file.size / chunkSize);

        let totalUploaded = 0;

        for (let i = 0; i < totalChunks; i++) {
            const start = i * chunkSize;
            const end = Math.min(file.size, start + chunkSize);
            const chunk = file.slice(start, end);

            try {
                let total = 0;
                await submitDocument(chunk, fileName, i + 1, (progressEvent) => {
                    total = progressEvent.total;
                    const overallProgress = Math.round(
                    (totalUploaded * 100) / file.size +
                    (progressEvent.loaded * 100) / file.size
                    );
                    setUploadProgress(overallProgress);
                });
                totalUploaded += total;
            } catch (error) {
                console.error(`❌ Chunk ${i + 1} failed`, error);
                // setUploadedFileError(true);
                setFormStatus(false);
                setUploadedFile("Upload file here"); // ✅ reset label if failed
                CustomToast.warning("Upload failed. Please check your internet connection and try again.");
                return; // stop further chunks
            }
        }

        try {
            await combineDocument(fileName, extension, totalChunks);
            setUploadProgress(0);
            setUploadedFileError(false);
        } catch (error) {
            // setUploadedFileError(true);
            setFormStatus(false);
            setUploadedFile("Upload file here"); // ✅ reset label if failed
            CustomToast.defaultError();
        }
    };
    
    const imageValidations = file => validateFile(file, 'image');
    const videoValidations = file => validateFile(file, 'video');
    const audioValidations = file => validateFile(file, 'audio');
    const documentValidations = file => validateFile(file, 'document');
    const documentPDFValidations = file => validateFile(file, 'pdf');

    const submitFile = (file, fileName) => {
        setFormStatus(true);
        const dataArray = new FormData();
        dataArray.append('file', file);
        
        postFile(dataArray).then(response => {
            const status = response.data.status;
            if (status === 'Success') {
                setFormStatus(false);
                setUploadedFile(fileName);

                const data = response.data.data.url;
                if (data) {
                    setItem({ name: 'File', value: data });
                }
            } else {
                CustomToast.defaultError();
                setFormStatus(false);
            }
        }).catch(() => {  
            CustomToast.defaultError();
            setFormStatus(false);
        });
    }

    const handleUpload = () => {
        if (item.value === '') {
            setUploadedFileError(true);
            setFormStatus(false);
            return;
        } else {
            onData({ target: item });
            onHide();
            setUploadedFile('Upload file here');
        }
    }

    const toggleHide = () => {
        onHide();
        setItem({ name: '', value: '' });
        setUploadedFile('Upload file here');
        setUploadedFileError(false);
    }

    return (
        <>
            <Modal
                isOpen={show}
                className="modal-preview"
                fade={false}
                centered
                id="edit-judge-modal"
            >
            <ModalHeader className="text-left">
                UPLOAD {uploadInstanceLabel[instanceType]}
                {!formStatus && (
                <button
                    type="button"
                    className="close react-modal-close"
                    data-dismiss="modal"
                    aria-label="Close"
                    onClick={() => toggleHide()}
                >
                    <span aria-hidden="true">&times;</span>
                </button>
                )}
            </ModalHeader>
            <ModalBody>
                <Row>
                    <Col lg="12">
                        <Card>
                            <CardBody className="card_container">
                                <Label
                                    onClick={handleClickFile}
                                    onDragEnter={handleDragEnter}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    className={`${uploadedFileError
                                        ? 'download_icon form-invalid'
                                        : 'download_icon'
                                        }`}
                                    style={
                                        formStatus
                                        ? { cursor: 'not-allowed', opacity: '0.5' }
                                        : {}
                                    }
                                >
                                    <img src={DownloadIcon} draggable={false}/>
                                    <br />
                                    {uploadedFile}
                                </Label>
                                {uploadedFileError ? (
                                    <p role="alert" className="invalid-feedback">
                                        File is required
                                    </p>
                                ) : null}
                                <input
                                    type="file"
                                    ref={hiddenFileInputPdf}
                                    accept={
                                        instanceType === 'image'
                                        // ? '.jpg, .jpeg, .png, .gif, .bmp, .webp, .svg'
                                        ? '.jpeg, .png, .jpg, .gif'
                                        : instanceType === 'video'
                                        // ? '.mp4, .avi, .mov, .wmv, .flv, .mkv, .webm'
                                        ? '.mp4, .mov, .avi'
                                        : instanceType === 'audio'
                                        // ? '.mp3, .wav, .ogg, .aac, .flac, .m4a'
                                        ? '.mp3, .wav'
                                        : instancePDFType === 'pdf'
                                        ? '.pdf'
                                        : instanceType === 'document'
                                        // ? '.pdf, .doc, .docx, .odt, .rtf, .txt, .xls, .xlsx, .csv, .ods, .ppt, .pptx, .odp, .zip, .rar, .7z, .tar, .gz,  .html, .htm, .css, .js, .json, .xml, .php, .py, .java, .c, .cpp'
                                        ? '.pdf, .doc, .docx, .xlsx, .csv'
                                        : 'image/*'
                                    }
                                    onChange={handleChangeFiles}
                                    style={{ display: 'none' }}
                                    required
                                    disabled={formStatus}
                                />
                            </CardBody>
                            <CardFooter className="text-right">
                                {!formStatus && (
                                <button
                                    type="button"
                                    className="btn btn-main btn-main-alt mr-2"
                                    data-dismiss="modal"
                                    onClick={() => toggleHide()}
                                >
                                    CLOSE
                                </button>
                                )}
                                {!formStatus ? (
                                <button
                                    type="button"
                                    className="newbuttontheme w-25"
                                    onClick={handleUpload}
                                >
                                    SAVE
                                </button>
                                ) : (
                                <button
                                    type="button"
                                    className="newbuttontheme w-25"
                                >
                                    UPLOADING...
                                </button>
                                )}
                            </CardFooter>
                        </Card>
                    </Col>
                </Row>
            </ModalBody>
            </Modal>
        </>
    )
}

export default FileUploaderModal